import { useState } from 'react'
import { PageHeader, Panel, Button, Select, Switch, Chip, Banner, Textarea, FormRow, EmptyState, useToast } from '../ds/index'
import { useApi } from '../lib/useApi'
import { post, patch, put } from '../lib/api'
import { dateShort } from '../lib/format'
import { LoadingBlock, ErrorBlock } from '../components/Loading'

type Zone = { id: string; name: string; cities: string[]; neighborhoods: string[]; zips: string[]; active: boolean }
type Holder = { id: string; zone_id: string; category_id: string; subscriber_id: string; business: string; status: 'requested' | 'approved'; note: string | null; requested_at: string; decided_at: string | null; business_status: string }
type Data = {
  zones: Zone[]; holders: Holder[]; categories: { id: string; name: string }[]; slots: { zone_id: string; category_id: string; slots: number }[]
  businesses: { id: string; name: string; category_id: string | null; status: string }[]
  zone_routing: boolean; location_min_confidence: 'high' | 'medium' | 'low'
}

const CONFIDENCE = {
  high: 'High: only their profile, their own answer, or a place named in the post',
  medium: "Medium: also the rep's Muse note, or a place anywhere in the post (recommended)",
  low: "Low: also where they grew up, or the group's home zone",
}
const lines = (v: string) => v.split(/[\n,]/).map((x) => x.trim()).filter(Boolean)

/**
 * Service Zones (Business Terms 1.13, 2.3 to 2.7). One business per trade per
 * zone; a lead goes only to the business holding its trade where the person is.
 */
export default function Zones() {
  const { data, error, reload } = useApi<Data>('/api/zones')
  const toast = useToast()
  if (error) return <ErrorBlock error={error} />
  if (!data) return <LoadingBlock />
  const requests = data.holders.filter((h) => h.status === 'requested')

  const setting = async (body: Partial<Data>) => {
    try { await patch('/api/settings', body); toast('Saved', 'pos'); reload() } catch (e) { toast((e as Error).message, 'neg') }
  }
  const starter = async () => {
    try { await post('/api/zones/starter', {}); toast('One zone per city made. Add ZIP codes and neighborhoods to each.', 'pos'); reload() }
    catch (e) { toast((e as Error).message, 'neg') }
  }

  return (
    <>
      <PageHeader title="Zones" subtitle="Where each business gets its leads. One business per trade per zone; a lead goes to the business holding its trade where the person is." />
      <div className="stack">
        <Panel>
          <div className="panel-header"><h2 className="panel-title">Routing</h2></div>
          <div className="stack-tight">
            <Switch label={data.zone_routing ? 'Leads go by zone' : 'Leads go by group and area (zones off)'} checked={data.zone_routing}
                    onChange={(e) => setting({ zone_routing: e.target.checked })} />
            <FormRow label="How sure the location must be before a lead goes out" help="Below this, the lead waits in Location unknown on the lead board until someone picks the zone. Not sent and not billed meanwhile.">
              <Select value={data.location_min_confidence} onChange={(e) => setting({ location_min_confidence: e.target.value as Data['location_min_confidence'] })}>
                {(Object.keys(CONFIDENCE) as (keyof typeof CONFIDENCE)[]).map((k) => <option key={k} value={k}>{CONFIDENCE[k]}</option>)}
              </Select>
            </FormRow>
          </div>
        </Panel>

        {requests.length > 0 && (
          <Panel>
            <div className="panel-header"><h2 className="panel-title">Asking for a zone</h2><span className="muted">{requests.length} waiting</span></div>
            <div className="stack-tight">
              {requests.map((h) => <Request key={h.id} h={h} data={data} onDone={reload} />)}
            </div>
          </Panel>
        )}

        {data.zones.length === 0 ? (
          <Panel>
            <EmptyState title="No zones yet" body="Start with one zone per Phoenix-area city, then add the ZIP codes and neighborhoods each one covers." />
            <div style={{ textAlign: 'center' }}><Button onClick={starter}>Start with Phoenix-area cities</Button></div>
          </Panel>
        ) : (
          <>
            <NewZone onSaved={reload} />
            {data.zones.map((z) => <ZoneCard key={z.id} zone={z} data={data} onSaved={reload} />)}
          </>
        )}
      </div>
    </>
  )
}

function Request({ h, data, onDone }: { h: Holder; data: Data; onDone: () => void }) {
  const toast = useToast()
  const [note, setNote] = useState('')
  const zone = data.zones.find((z) => z.id === h.zone_id)
  const trade = data.categories.find((c) => c.id === h.category_id)
  const holder = data.holders.find((x) => x.zone_id === h.zone_id && x.category_id === h.category_id && x.status === 'approved')
  const decide = async (what: 'approve' | 'decline') => {
    try { const r = await post<{ released?: number }>(`/api/zone-holders/${h.id}/${what}`, { note }); toast(what === 'approve' ? `Approved${r.released ? `, and ${r.released} waiting lead${r.released === 1 ? '' : 's'} sent to them` : ''}` : 'Declined, and the business was told', 'pos'); onDone() }
    catch (e) { toast((e as Error).message, 'neg') }
  }
  return (
    <div className="zone-request">
      <div><strong>{h.business}</strong> asks for <strong>{zone?.name}</strong> ({trade?.name}) <span className="muted">· {dateShort(h.requested_at)}</span>
        {h.note && <div className="muted">"{h.note}"</div>}
        {holder && <div className="tone-warn">{holder.business} holds it now. Approving needs a free slot.</div>}
      </div>
      <div className="row-tight" style={{ flexWrap: 'wrap' }}>
        <input className="field" placeholder="Note to the business (optional)" value={note} onChange={(e) => setNote(e.target.value)} style={{ minWidth: 200 }} />
        <Button onClick={() => decide('approve')}>Approve</Button>
        <Button variant="ghost" onClick={() => decide('decline')}>Decline</Button>
      </div>
    </div>
  )
}

function NewZone({ onSaved }: { onSaved: () => void }) {
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  if (!open) return <div><Button variant="secondary" onClick={() => setOpen(true)}>Add a zone</Button></div>
  const save = async () => {
    try { await post('/api/zones', { name, cities: [name] }); setOpen(false); setName(''); toast('Zone added. Add its ZIP codes and neighborhoods.', 'pos'); onSaved() }
    catch (e) { toast((e as Error).message, 'neg') }
  }
  return (
    <Panel>
      <div className="row-tight" style={{ flexWrap: 'wrap' }}>
        <input className="field" placeholder="Zone name, like North Scottsdale" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        <Button onClick={save} disabled={!name.trim()}>Add</Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </Panel>
  )
}

function ZoneCard({ zone, data, onSaved }: { zone: Zone; data: Data; onSaved: () => void }) {
  const toast = useToast()
  const [editing, setEditing] = useState(false)
  const [f, setF] = useState({ name: zone.name, cities: zone.cities.join(', '), neighborhoods: zone.neighborhoods.join(', '), zips: zone.zips.join(', ') })
  const [adding, setAdding] = useState({ category_id: '', subscriber_id: '' })
  const holders = data.holders.filter((h) => h.zone_id === zone.id && h.status === 'approved')
  const slotsOf = (cat: string) => data.slots.find((s) => s.zone_id === zone.id && s.category_id === cat)?.slots || 1

  const save = async () => {
    try {
      await patch(`/api/zones/${zone.id}`, { name: f.name, cities: lines(f.cities), neighborhoods: lines(f.neighborhoods), zips: lines(f.zips) })
      setEditing(false); toast('Zone saved', 'pos'); onSaved()
    } catch (e) { toast((e as Error).message, 'neg') }
  }
  const add = async () => {
    try { const r = await post<{ released?: number }>(`/api/zones/${zone.id}/holders`, adding); setAdding({ category_id: '', subscriber_id: '' }); toast(`Holder set${r.released ? `, and ${r.released} waiting lead${r.released === 1 ? '' : 's'} sent to them` : ''}`, 'pos'); onSaved() }
    catch (e) { toast((e as Error).message, 'neg') }
  }
  const end = async (id: string) => {
    try { await post(`/api/zone-holders/${id}/end`, {}); toast('Ended. Leads for this trade here now go to the Unassigned list.', 'pos'); onSaved() }
    catch (e) { toast((e as Error).message, 'neg') }
  }
  const slot = async (cat: string, n: number) => {
    try { await put(`/api/zones/${zone.id}/slots/${cat}`, { slots: n }); toast(n > 1 ? 'Second slot opened' : 'Back to one business', 'pos'); onSaved() }
    catch (e) { toast((e as Error).message, 'neg') }
  }
  const toggle = async () => {
    try { await patch(`/api/zones/${zone.id}`, { active: !zone.active }); onSaved() } catch (e) { toast((e as Error).message, 'neg') }
  }
  const candidates = data.businesses.filter((b) => b.status === 'active' && (!adding.category_id || b.category_id === adding.category_id))
  const places = [...zone.cities, ...zone.neighborhoods].join(', ')

  return (
    <Panel>
      <div className="panel-header">
        <h2 className="panel-title">{zone.name}{!zone.active && <span className="muted"> (off)</span>}</h2>
        <div className="row-tight">
          <Button variant="ghost" onClick={() => setEditing(!editing)}>{editing ? 'Close' : 'Edit places'}</Button>
          <Button variant="ghost" onClick={toggle}>{zone.active ? 'Switch off' : 'Switch on'}</Button>
        </div>
      </div>
      {editing ? (
        <div className="stack-tight">
          <FormRow label="Name"><input className="field" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></FormRow>
          <FormRow label="Cities" help="Separate with commas."><Textarea rows={2} value={f.cities} onChange={(e) => setF({ ...f, cities: e.target.value })} /></FormRow>
          <FormRow label="Neighborhoods" help="Names people write in posts, like Arcadia or Ahwatukee."><Textarea rows={2} value={f.neighborhoods} onChange={(e) => setF({ ...f, neighborhoods: e.target.value })} /></FormRow>
          <FormRow label="ZIP codes" help="A ZIP decides first, so a city split between two zones still sends each lead to the right one."><Textarea rows={2} value={f.zips} onChange={(e) => setF({ ...f, zips: e.target.value })} /></FormRow>
          <div><Button onClick={save}>Save zone</Button></div>
        </div>
      ) : (
        <p className="muted" style={{ marginTop: 0 }}>{places || 'No places yet'}{zone.zips.length ? ` · ${zone.zips.length} ZIP code${zone.zips.length === 1 ? '' : 's'}` : ' · no ZIP codes'}</p>
      )}
      <div className="stack-tight">
        {holders.length === 0 && <span className="muted">Nobody holds a trade here yet.</span>}
        {holders.map((h) => {
          const trade = data.categories.find((c) => c.id === h.category_id)
          const n = slotsOf(h.category_id)
          return (
            <div key={h.id} className="row-tight" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <span><Chip tone="accent">{trade?.name}</Chip> {h.business}{h.business_status !== 'active' && <span className="tone-warn"> ({h.business_status}: sent no leads)</span>}</span>
              <span className="row-tight">
                <Button variant="ghost" onClick={() => slot(h.category_id, n > 1 ? 1 : 2)}>{n > 1 ? 'Close the second slot' : 'Open a second slot'}</Button>
                <Button variant="ghost" onClick={() => end(h.id)}>End</Button>
              </span>
            </div>
          )
        })}
        <div className="row-tight" style={{ flexWrap: 'wrap', borderTop: '1px solid var(--border)', paddingTop: 'var(--s-3)' }}>
          <Select aria-label="Trade" value={adding.category_id} onChange={(e) => setAdding({ category_id: e.target.value, subscriber_id: '' })}>
            <option value="">Trade</option>
            {data.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Select aria-label="Business" value={adding.subscriber_id} onChange={(e) => setAdding({ ...adding, subscriber_id: e.target.value })}>
            <option value="">Business</option>
            {candidates.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
          <Button variant="secondary" onClick={add} disabled={!adding.subscriber_id}>Give them this zone</Button>
        </div>
      </div>
      {!zone.active && <Banner tone="warn">Switched off: no lead is placed in this zone.</Banner>}
    </Panel>
  )
}
